import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Calendar, Users, AlertCircle, ChevronLeft, ChevronRight, 
  Clock, CheckCircle2, AlertTriangle, Cpu, Wrench, RefreshCw, ChevronDown, Layers
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';

const SmartScheduler = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const session = JSON.parse(localStorage.getItem('user') || '{}');

  // --- States ---
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('WEEKLY'); // 'WEEKLY' or 'QUARTERLY'
  
  const [workOrders, setWorkOrders] = useState([]);
  const [labors, setLabors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  
  // States للـ Tooltip
  const [hoveredWO, setHoveredWO] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // State لشجرة العمال
  const [expandedCrafts, setExpandedCrafts] = useState({});

  // --- Date Math & Columns ---
  const columnsData = useMemo(() => {
    const cols = [];
    const baseDate = new Date(currentDate);
    baseDate.setHours(0, 0, 0, 0);

    if (viewMode === 'WEEKLY') {
      baseDate.setDate(baseDate.getDate() - baseDate.getDay()); 
      for (let i = 0; i < 7; i++) {
        cols.push({ 
          date: new Date(baseDate), 
          label: baseDate.toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric' }) 
        });
        baseDate.setDate(baseDate.getDate() + 1);
      }
    } else {
      baseDate.setDate(baseDate.getDate() - baseDate.getDay()); 
      for (let i = 0; i < 12; i++) {
        const weekEnd = new Date(baseDate);
        weekEnd.setDate(weekEnd.getDate() + 6);
        cols.push({ 
          date: new Date(baseDate), 
          label: `${baseDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString(i18n.language, { day: 'numeric' })}`
        });
        baseDate.setDate(baseDate.getDate() + 7);
      }
    }
    return cols;
  }, [currentDate, viewMode, i18n.language]);

  const moveTime = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'WEEKLY') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setDate(newDate.getDate() + (direction * 7 * 12));
    }
    setCurrentDate(newDate);
  };

  const formatHeaderRange = () => {
    const start = columnsData[0].date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
    const endDate = new Date(columnsData[columnsData.length - 1].date.getTime() + (viewMode === 'WEEKLY' ? 0 : 6 * 24 * 60 * 60 * 1000));
    const end = endDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} — ${end}`;
  };

  // --- Fetch Data ---
  const fetchData = useCallback(async () => {
    if (!session.org_id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/scheduler/data?org_id=${session.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWorkOrders(res.data.workorders || []);
      setLabors(res.data.labors || []);
    } catch (err) {
      toast.error(t('scheduler.fetch_error', 'Failed to load scheduling data'));
    } finally {
      setLoading(false);
    }
  }, [session.org_id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Drag and Drop Handlers ---
  const handleDragStart = (e, wo) => {
    e.dataTransfer.setData('wo_id', wo.id);
    e.dataTransfer.setData('is_forecast', wo.is_forecast ? 'true' : 'false');
    e.dataTransfer.effectAllowed = 'move';
    setHoveredWO(null); 
  };

  const handleDragOver = (e) => {
    e.preventDefault(); 
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnTime = async (e, targetDate) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('wo_id');
    const isForecast = e.dataTransfer.getData('is_forecast') === 'true';
    if (!id) return;

    const targetDateStr = `${targetDate.getFullYear()}-${String(targetDate.getMonth()+1).padStart(2,'0')}-${String(targetDate.getDate()).padStart(2,'0')}`;
    const loadId = toast.loading(`Moving ${id}...`);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/scheduler/reschedule', {
        wonum: id, 
        new_date: targetDateStr, 
        is_forecast: isForecast, 
        org_id: session.org_id
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      
      toast.success(`Rescheduled to ${targetDateStr}`, { id: loadId });
      fetchData(); 
    } catch (err) { 
      toast.error(`Failed to reschedule`, { id: loadId }); 
    }
  };

  const handleDropOnLabor = async (e, targetDate, laborcode) => {
    e.preventDefault(); 
    e.stopPropagation();
    const id = e.dataTransfer.getData('wo_id');
    const isForecast = e.dataTransfer.getData('is_forecast') === 'true';
    if (!id) return;

    const targetDateStr = `${targetDate.getFullYear()}-${String(targetDate.getMonth()+1).padStart(2,'0')}-${String(targetDate.getDate()).padStart(2,'0')}`;
    const loadId = toast.loading(`Dispatching to ${laborcode}...`);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/scheduler/assign', {
        wonum: id, 
        laborcode: laborcode, 
        target_date: targetDateStr, 
        is_forecast: isForecast, 
        org_id: session.org_id
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      
      toast.success(`Dispatched to ${laborcode}`, { id: loadId });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Dispatch failed`, { id: loadId });
    }
  };

  // --- AI Optimize ---
  const handleAIOptimize = async () => {
    setIsOptimizing(true);
    const loadId = toast.loading(t('scheduler.optimizing', 'AI is leveling resource load...'));
    try {
      const token = localStorage.getItem('token');
      
      const startDateStr = `${columnsData[0].date.getFullYear()}-${String(columnsData[0].date.getMonth() + 1).padStart(2, '0')}-${String(columnsData[0].date.getDate()).padStart(2, '0')}`;
      
      const lastDate = new Date(columnsData[columnsData.length - 1].date);
      if (viewMode === 'QUARTERLY') lastDate.setDate(lastDate.getDate() + 6);
      const endDateStr = `${lastDate.getFullYear()}-${String(lastDate.getMonth() + 1).padStart(2, '0')}-${String(lastDate.getDate()).padStart(2, '0')}`;

      const payload = {
        start_date: startDateStr,
        end_date: endDateStr,
        org_id: session.org_id
      };

      const res = await axios.post('/api/scheduler/optimize', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(res.data.message, { id: loadId });
      fetchData(); 
    } catch (err) {
      toast.error(t('scheduler.optimize_fail', 'AI Optimization failed.'), { id: loadId });
    } finally {
      setIsOptimizing(false);
    }
  };

  // --- Dynamic Processing ---
  const displayedWOs = useMemo(() => {
    const startTime = columnsData[0].date.getTime();
    const endTime = columnsData[columnsData.length - 1].date.getTime() + (viewMode === 'WEEKLY' ? 23*59*59*1000 : 6*24*60*60*1000); 
    const colCount = columnsData.length;
    
    return workOrders.filter(wo => {
      const effectiveDateStr = wo.scheddate || wo.targstartdate;
      if (!effectiveDateStr) return false;
      const parts = effectiveDateStr.split('T')[0].split('-'); 
      const woDate = new Date(parts[0], parts[1] - 1, parts[2]).getTime();
      return woDate >= startTime && woDate <= endTime;
    }).map(wo => {
      const effectiveDateStr = wo.scheddate || wo.targstartdate;
      const parts = effectiveDateStr.split('T')[0].split('-'); 
      const woDate = new Date(parts[0], parts[1] - 1, parts[2]);
      
      let index = 0;
      let durationUnits = Math.ceil((wo.estdur || 8) / 8.0); 

      if (viewMode === 'WEEKLY') {
        index = woDate.getDay();
      } else {
        const diffTime = Math.abs(woDate - columnsData[0].date);
        index = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
        durationUnits = durationUnits / 7.0; 
      }

      return {
        ...wo,
        colIndex: index,
        renderWidth: Math.min(durationUnits, colCount - index), 
        isEmergency: wo.priority === 1
      };
    });
  }, [workOrders, columnsData, viewMode]);

  const resourceHeatmap = useMemo(() => {
    const craftMap = {};
    const multiplier = viewMode === 'WEEKLY' ? 1 : 5; // أسبوع العمل 5 أيام = 40 ساعة
    const colCount = columnsData.length;

    labors.forEach(labor => {
      const craft = labor.craft || 'UNASSIGNED';
      if (!craftMap[craft]) {
        craftMap[craft] = {
          name: craft,
          daily_capacity: 0,
          load: Array(colCount).fill(0),
          individuals: []
        };
      }
      craftMap[craft].daily_capacity += (8.0 * multiplier);
      craftMap[craft].individuals.push({
        laborcode: labor.laborcode,
        name: labor.name || labor.laborcode,
        capacity: 8.0 * multiplier,
        load: Array(colCount).fill(0)
      });
    });

    displayedWOs.forEach(wo => {
      Object.entries(wo.craft_needs || {}).forEach(([craft, hours]) => {
        if (craftMap[craft] && wo.colIndex < colCount) {
          craftMap[craft].load[wo.colIndex] += hours;
        }
      });

      if (wo.assignments && wo.assignments.length > 0) {
        wo.assignments.forEach(assign => {
          Object.values(craftMap).forEach(craftObj => {
            const laborNode = craftObj.individuals.find(ind => ind.laborcode === assign.laborcode);
            if (laborNode && wo.colIndex < colCount) {
              laborNode.load[wo.colIndex] += assign.hrs;
            }
          });
        });
      }
    });

    return Object.values(craftMap);
  }, [labors, displayedWOs, columnsData, viewMode]);

  const toggleCraft = (craftName) => {
    setExpandedCrafts(prev => ({ ...prev, [craftName]: !prev[craftName] }));
  };

  // --- KPIs ---
  const kpis = [
    { title: t('scheduler.kpi_scheduled', "Scheduled Work"), value: displayedWOs.length, icon: <Calendar size={18}/>, color: "text-blue-600", bg: "bg-blue-50" },
    { title: t('scheduler.kpi_issues', "Unscheduled"), value: workOrders.filter(w => !(w.scheddate || w.targstartdate)).length, icon: <AlertTriangle size={18}/>, color: "text-orange-500", bg: "bg-orange-50" },
    { title: t('scheduler.kpi_utilization', "Total Resources"), value: labors.length, icon: <Users size={18}/>, color: "text-green-600", bg: "bg-green-50" },
    { title: t('scheduler.kpi_emergencies', "Emergencies"), value: displayedWOs.filter(wo => wo.isEmergency && !wo.is_forecast).length, icon: <AlertCircle size={18}/>, color: "text-red-600", bg: "bg-red-50", pulse: displayedWOs.some(wo => wo.isEmergency && !wo.is_forecast) }
  ];

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] ${isRTL ? 'font-cairo' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* 1️⃣ Toolbar */}
      <div className="bg-white border-b border-gray-200 p-3 px-6 flex justify-between items-center shrink-0 z-20 shadow-sm">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-black text-[#1a3353] flex items-center gap-2 uppercase tracking-tighter">
            <Clock className="text-[#00a1df]" size={22} />
            {t('scheduler.title', 'Dispatching & Scheduling')}
          </h1>
          <div className="h-6 w-[1px] bg-gray-300"></div>

          {/* 🎯 أزرار التبديل بين أسبوعي وربع سنوي */}
          <div className="flex bg-slate-100 rounded-md p-1 border border-slate-200">
            <button 
              onClick={() => setViewMode('WEEKLY')} 
              className={`px-3 py-1 font-black rounded text-[10px] uppercase transition-all ${viewMode === 'WEEKLY' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Weekly
            </button>
            <button 
              onClick={() => setViewMode('QUARTERLY')} 
              className={`px-3 py-1 font-black rounded text-[10px] uppercase transition-all ${viewMode === 'QUARTERLY' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Quarterly
            </button>
          </div>

          <div className="flex items-center bg-slate-50 border border-slate-200 rounded-md p-1 ml-2">
            <button onClick={() => moveTime(-1)} className="p-1 hover:bg-white rounded shadow-sm text-slate-500"><ChevronLeft size={16}/></button>
            <span className="px-4 font-bold text-slate-700 min-w-[150px] text-center">{formatHeaderRange()}</span>
            <button onClick={() => moveTime(1)} className="p-1 hover:bg-white rounded shadow-sm text-slate-500"><ChevronRight size={16}/></button>
          </div>
          <button onClick={fetchData} className="p-2 hover:bg-blue-50 text-blue-600 rounded-full transition-all">
             <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={handleAIOptimize}
            disabled={isOptimizing}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-4 py-2 rounded-md font-black flex items-center gap-2 shadow-md transition-all disabled:opacity-50"
          >
            <Cpu size={16} className={isOptimizing ? "animate-pulse" : ""} /> 
            {isOptimizing ? t('common.processing', 'PROCESSING...') : t('scheduler.ai_optimize', 'AI AUTO-OPTIMIZE')}
          </button>
        </div>
      </div>

      {/* 2️⃣ KPI Dashboard */}
      <div className="grid grid-cols-4 gap-4 p-4 shrink-0">
        {kpis.map((kpi, idx) => (
          <div key={idx} className="bg-white p-4 rounded-xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex items-center gap-4 relative overflow-hidden group">
            <div className={`w-12 h-12 rounded-full ${kpi.bg} ${kpi.color} flex items-center justify-center shrink-0`}>
              {kpi.icon}
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{kpi.title}</span>
              <span className="text-2xl font-black text-slate-700">{kpi.value}</span>
            </div>
            {kpi.pulse && <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>}
          </div>
        ))}
      </div>

      {/* 3️⃣ Gantt Area */}
      <div className="flex-1 flex flex-col mx-4 mb-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative">
        
        {/* Header Row */}
        <div className="flex border-b border-gray-200 bg-slate-50 shrink-0">
          <div className={`w-80 p-3 font-black text-slate-500 uppercase tracking-widest text-[10px] ${isRTL ? 'border-l' : 'border-r'} border-gray-200`}>
            Work & Forecasts
          </div>
          <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${columnsData.length}, minmax(0, 1fr))` }}>
            {columnsData.map((col, idx) => {
              const isToday = new Date().toDateString() === col.date.toDateString() && viewMode === 'WEEKLY';
              return (
                <div key={idx} className={`p-2 text-center font-black uppercase text-[10px] ${idx !== columnsData.length - 1 ? (isRTL ? 'border-l' : 'border-r') : ''} border-gray-200 ${isToday ? 'bg-blue-100 text-[#005495]' : 'text-slate-600'}`}>
                  {col.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* WO Grid */}
        <div className="flex-1 overflow-y-auto custom-scrollbar relative">
          {displayedWOs.length === 0 ? (
            <div className="p-10 text-center text-gray-400 font-black italic">{t('scheduler.no_data', 'No Scheduled Work Orders')}</div>
          ) : (
            displayedWOs.map((wo, idx) => (
              <div key={idx} className="flex border-b border-gray-100 group hover:bg-blue-50/30 transition-colors">
                <div className={`w-80 p-3 ${isRTL ? 'border-l' : 'border-r'} border-gray-200 flex flex-col justify-center gap-1 bg-white group-hover:bg-transparent`}>
                  <div className="flex justify-between items-center">
                    <span className={`font-black ${wo.is_forecast ? 'text-purple-600' : 'text-[#005495]'} text-[12px] flex items-center gap-1`}>
                      {wo.is_forecast ? <Layers size={12} /> : (wo.isEmergency && <AlertCircle size={12} className="animate-pulse text-red-600" />)}
                      {wo.wonum}
                    </span>
                    <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 rounded uppercase">{Object.keys(wo.craft_needs || {}).join(', ') || 'GENERAL'}</span>
                  </div>
                  <span className="font-bold text-gray-600 truncate">{wo.description}</span>
                </div>

                <div className="flex-1 grid relative" style={{ gridTemplateColumns: `repeat(${columnsData.length}, minmax(0, 1fr))` }}>
                  {columnsData.map((col, i) => (
                    <div 
                      key={i} 
                      className={`h-full ${i !== columnsData.length - 1 ? (isRTL ? 'border-l' : 'border-r') : ''} border-gray-100/50 hover:bg-blue-50/50 transition-colors`}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDropOnTime(e, col.date)}
                    ></div>
                  ))}
                  
                  {/* Draggable Bar */}
                  <div 
                    draggable
                    onDragStart={(e) => handleDragStart(e, wo)}
                    onMouseEnter={(e) => { setHoveredWO(wo); setMousePos({ x: e.clientX, y: e.clientY }); }}
                    onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHoveredWO(null)}
                    className={`absolute top-2 bottom-2 rounded-md shadow-sm opacity-90 hover:opacity-100 cursor-grab active:cursor-grabbing flex items-center px-2 text-white font-bold text-[9px] overflow-hidden transition-all hover:scale-[1.01] 
                      ${wo.is_forecast ? 'bg-purple-500 bg-[repeating-linear-gradient(45deg,transparent,transparent_5px,rgba(255,255,255,0.2)_5px,rgba(255,255,255,0.2)_10px)]' : (wo.isEmergency ? 'bg-red-500' : 'bg-[#00a1df]')}`}
                    style={{ 
                      [isRTL ? 'right' : 'left']: `calc(${wo.colIndex * (100 / columnsData.length)}% + 4px)`, 
                      width: `calc(${Math.max(0.5, wo.renderWidth) * (100 / columnsData.length)}% - 8px)` 
                    }}
                  >
                    {wo.status}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 4️⃣ Dispatching Board (Heatmap) */}
        <div className="h-64 border-t-2 border-slate-300 bg-slate-50 flex flex-col shrink-0">
          <div className="p-2 bg-slate-100 border-b border-gray-200 font-black text-slate-500 uppercase tracking-widest text-[10px] flex items-center gap-2">
            <Users size={14} /> Dispatching Board (Cap: {viewMode === 'WEEKLY' ? '8H/Day' : '40H/Week'})
          </div>
          <div className="flex-1 overflow-y-auto">
             {resourceHeatmap.length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-[10px] font-bold italic">{t('scheduler.no_resources', 'No resource capacity data.')}</div>
             ) : (
              resourceHeatmap.map((res, idx) => (
                <React.Fragment key={idx}>
                  <div 
                    className="flex border-b border-gray-200/50 bg-white hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => toggleCraft(res.name)}
                  >
                    <div className={`w-80 p-2 ${isRTL ? 'border-l pl-4' : 'border-r pr-4'} border-gray-200 flex items-center justify-between font-black text-[#1a3353] text-[11px] uppercase`}>
                      <div className="flex items-center gap-2">
                        <ChevronDown size={14} className={`text-gray-400 transition-transform ${expandedCrafts[res.name] ? 'rotate-180' : ''}`} />
                        {res.name}
                      </div>
                      <span className="text-gray-400 bg-gray-100 px-1.5 rounded-sm">Cap: {res.daily_capacity}h</span>
                    </div>
                    <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${columnsData.length}, minmax(0, 1fr))` }}>
                      {res.load.map((workload, i) => {
                        let bgColor = "bg-green-100/30 text-green-700"; 
                        if (workload === res.daily_capacity && res.daily_capacity > 0) bgColor = "bg-blue-100 text-blue-800"; 
                        if (workload > res.daily_capacity) bgColor = "bg-red-200/80 text-red-800 font-black"; 

                        return (
                          <div key={i} className={`p-1.5 ${i !== columnsData.length - 1 ? (isRTL ? 'border-l' : 'border-r') : ''} border-gray-100`}>
                            <div className={`h-full w-full rounded flex items-center justify-center text-[10px] font-mono transition-all cursor-help ${bgColor}`}>
                              {workload > 0 ? `${workload}h` : '-'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {expandedCrafts[res.name] && res.individuals.map((labor, lIdx) => (
                    <div key={`l-${lIdx}`} className="flex border-b border-slate-100 bg-slate-50/50 hover:bg-blue-50/20 transition-colors">
                      <div className={`w-80 p-2 py-1.5 ${isRTL ? 'border-l pr-8' : 'border-r pl-8'} border-gray-200 flex items-center justify-between font-bold text-slate-500 text-[10px]`}>
                        <span className="flex items-center gap-1.5"><Users size={12} className="text-blue-400"/> {labor.name}</span>
                        <span className="text-gray-400">{viewMode === 'WEEKLY' ? '8h' : '40h'}</span>
                      </div>
                      <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${columnsData.length}, minmax(0, 1fr))` }}>
                        {labor.load.map((workload, i) => {
                          let bgColor = "bg-slate-100 text-slate-400 border border-slate-200 border-dashed"; 
                          const capLimit = viewMode === 'WEEKLY' ? 8 : 40;

                          if (workload > 0 && workload < capLimit) bgColor = "bg-green-100 text-green-700 border border-green-200"; 
                          if (workload === capLimit) bgColor = "bg-blue-200 text-blue-800 border border-blue-300"; 
                          if (workload > capLimit) bgColor = "bg-red-200 text-red-800 font-black border border-red-300 shadow-inner"; 

                          return (
                            <div 
                              key={i} 
                              className={`p-1 ${i !== columnsData.length - 1 ? (isRTL ? 'border-l' : 'border-r') : ''} border-slate-200/50`}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDropOnLabor(e, columnsData[i].date, labor.laborcode)}
                            >
                              <div className={`h-full w-full rounded flex items-center justify-center text-[9px] font-black transition-all ${bgColor}`}>
                                {workload > 0 ? `${workload}h` : 'DROP WO'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))
             )}
          </div>
        </div>

      </div>

      {/* 5️⃣ Tooltip Modal */}
      {hoveredWO && (
        <div 
          className="fixed pointer-events-none z-[9999] bg-white border border-slate-200 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] rounded-xl p-3 w-64 text-[11px] animate-in fade-in zoom-in-95 duration-100"
          style={{ top: mousePos.y + 15, left: isRTL ? mousePos.x - 260 : mousePos.x + 15 }}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <div className="flex justify-between items-center mb-2 border-b border-slate-100 pb-1.5">
            <span className={`font-black text-[12px] ${hoveredWO.is_forecast ? 'text-purple-700' : 'text-[#005495]'}`}>
              {hoveredWO.wonum}
            </span>
            <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase ${hoveredWO.is_forecast ? 'bg-purple-100 text-purple-700' : (hoveredWO.isEmergency ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-[#005495]')}`}>
              {hoveredWO.status}
            </span>
          </div>
          <div className="flex flex-col gap-1.5 text-slate-600">
            <p className="font-bold text-slate-800 line-clamp-2 leading-relaxed mb-1">{hoveredWO.description}</p>
            <div className="flex items-center justify-between"><span className="text-slate-400 font-bold uppercase text-[9px]">{t('wo.type', 'Type')}</span><span className="font-black text-slate-700">{hoveredWO.wo_type || 'N/A'}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-400 font-bold uppercase text-[9px]">{t('common.site', 'Site')}</span><span className="font-black text-slate-700">{hoveredWO.site_id || 'N/A'}</span></div>
            {(hoveredWO.assetnum || hoveredWO.location_id) && (
              <div className="flex items-center justify-between bg-slate-50 p-1 rounded"><span className="text-slate-400 font-bold uppercase text-[9px]">Target</span><span className="font-black text-[#00a1df]">{hoveredWO.assetnum || hoveredWO.location_id}</span></div>
            )}
            <div className="flex items-center justify-between"><span className="text-slate-400 font-bold uppercase text-[9px]">Est. Duration</span><span className="font-black text-slate-700">{hoveredWO.estdur} Hrs</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartScheduler;